import { useAccreditationStore } from "@/store/accreditation";
import { Menu } from "@headlessui/react";
import { ChevronDownIcon, LucideIcon } from "lucide-react";
import { ReactNode, useRef, useState } from "react";
import { ButtonSpinner } from "../ButtonSpinner";
import { useDarkMode } from "@/hooks";

export interface BulkAction {
  name: string;
  icon: LucideIcon;
  isDestructive?: boolean;
  handler: (selectedIds: Set<number>) => void;
  subActions?: Array<Partial<BulkAction>>;
  component?: React.ComponentType<any>;
}

interface Props {
  actions: Array<BulkAction>;
  selectedIds: Set<number>;
  type: "group" | "individual";
}

export const BulkActionsButton = ({ actions, selectedIds, type }: Props) => {
  const [actionRequiringConfirmation, setActionRequiringConfirmation] =
    useState<BulkAction | null>(null);
  const [actionWithComponent, setActionWithComponent] =
    useState<BulkAction | null>(null);
  const { bulkActionsLoading } = useAccreditationStore();
  // Add a ref to the Menu.Button to access the menu programmatically
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  // Helper to close the menu programmatically
  const closeMenu = () => {
    // Headless UI closes the menu when the button is clicked again
    if (menuButtonRef.current) {
      menuButtonRef.current.click();
    }
  };
  const isDarkMode = useDarkMode();

  return (
    <div className="inline-flex rounded-md shadow-sm">
      <button
        type="button"
        className={`dark:text-white hover:bg-gray-100 dark:hover:bg-zinc-800/80 dark:bg-zinc-800 bg-white dark:ring-white/10 relative inline-flex items-center px-3 my-1 text-xs font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 focus:z-10 ${
          bulkActionsLoading ? "py-1.5 rounded-md" : "rounded-l-md py-1"
        }`}
      >
        {bulkActionsLoading ? (
          <span className="flex">
            <ButtonSpinner colour={isDarkMode ? "white" : "black"} />
            Applying actions...
          </span>
        ) : (
          "Actions"
        )}
      </button>

      {!bulkActionsLoading && (
        <Menu as="div" className="relative -ml-px block">
          <Menu.Button
            ref={menuButtonRef}
            className="relative inline-flex items-center rounded-r-md py-1.5 my-1 px-1.5 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 hover:bg-gray-100 dark:hover:bg-zinc-800/80 dark:bg-zinc-800 bg-white dark:ring-white/10 focus:z-10"
          >
            <span className="sr-only">Open options</span>
            <ChevronDownIcon aria-hidden="true" className="size-4" />
          </Menu.Button>
          <Menu.Items
            // transition
            className="absolute right-0 z-10 mt-2 origin-top-right divide-y divide-gray-100 dark:divide-white/10 dark:bg-zinc-800 dark:ring-white/10 rounded-md bg-white shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
          >
            {actionWithComponent ? (
              // Render the component if present
              actionWithComponent.component ? (
                <actionWithComponent.component
                  selectedIds={selectedIds}
                  closeAction={() => {
                    setActionWithComponent(null);
                    closeMenu();
                  }}
                  type={type}
                />
              ) : null
            ) : actionRequiringConfirmation ? (
              <div className="min-w-48 text-center p-3">
                <div>
                  <h2 className="dark:text-white font-semibold text-sm text-zinc-600">
                    Are you sure?
                  </h2>
                  <p className="dark:text-zinc-500 text-zinc-600 text-xs mt-1">
                    Please confirm you want to perform this action
                  </p>
                </div>
                <div>
                  <div className="flex justify-center gap-2 mt-3">
                    <button
                      type="button"
                      onClick={() => setActionRequiringConfirmation(null)}
                      className="dark:text-white dark:bg-zinc-800 bg-gray-100 hover:bg-gray-200 dark:hover:bg-zinc-700/50 dark:ring-white/10 rounded-md px-3 py-1 text-xs font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 focus:z-10"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        actionRequiringConfirmation.handler(selectedIds);
                        setActionRequiringConfirmation(null);
                      }}
                      className="text-white dark:bg-red-600 bg-red-500 hover:bg-red-600 dark:ring-white/10 rounded-md px-3 py-1 text-xs font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 focus:z-10"
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="py-1">
                {actions.map((action) =>
                  action.subActions ? (
                    <div className="border-t dark:border-white/10 mt-1 pt-1 pr-1">
                      <div className="group dark:text-zinc-300 flex items-center px-4 py-2 text-sm text-gray-700 hover:text-zinc-500 whitespace-nowrap">
                        <span className="mr-3">
                          <action.icon
                            aria-hidden="true"
                            className="size-4 group-data-[focus]:text-gray-500"
                          />
                        </span>
                        {action.name}
                      </div>

                      <div className="ml-6 border-l mb-1 dark:border-white/10">
                        {action.subActions.map((subAction) => (
                          <Menu.Item key={subAction.name}>
                            <button
                              type="button"
                              onClick={(e) => {
                                if (subAction.isDestructive) {
                                  setActionRequiringConfirmation(subAction);
                                  e.preventDefault();
                                } else {
                                  subAction.handler(selectedIds);
                                }
                              }}
                              className="group dark:text-zinc-300 flex items-center px-4 py-2 text-sm text-gray-700 hover:text-zinc-500 whitespace-nowrap"
                            >
                              {subAction.name}
                            </button>
                          </Menu.Item>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Menu.Item key={action.name}>
                      <button
                        type="button"
                        onClick={(e) => {
                          if (action.isDestructive) {
                            setActionRequiringConfirmation(action);
                            e.preventDefault();
                          } else if (action.component) {
                            setActionWithComponent(action);
                            e.preventDefault();
                          } else {
                            action.handler(selectedIds);
                          }
                        }}
                        className="group dark:text-zinc-300 flex items-center px-4 py-2 text-sm text-gray-700 hover:text-zinc-500 whitespace-nowrap"
                      >
                        <span className="mr-3">
                          <action.icon
                            aria-hidden="true"
                            className="size-4 group-data-[focus]:text-gray-500"
                          />
                        </span>
                        {action.name}
                      </button>
                    </Menu.Item>
                  )
                )}
              </div>
            )}
          </Menu.Items>
        </Menu>
      )}
    </div>
  );
};
